/**
 * The Action Register runtime the shell provides (13-08): the writer for this build (practice
 * unless `VITE_QMS_LIVE_WRITES === 'true'`), the Graph I/O context, and the ONE outbox.
 *
 * The outbox belongs to the shell, not a page: the shell calls `replay()` on load and when the
 * tab becomes visible again, `flushAll()` when it is hidden, and prompts before unload while
 * anything is pending (13-06 contract, RESEARCH Pitfall 2). Pages enqueue / flush / undo on it.
 *
 * Provided by `components/ActionRuntimeProvider.tsx`. This file holds only the context and the
 * hook, so the provider file exports a component alone (react-refresh).
 */

import { createContext, useContext } from 'react';
import type { ActionIO } from '../services/actionService';
import type { OutboxBindings, Writer, WriteMode } from '../services/commitAdapter';
import type { Outbox } from '../lib/outbox';

export interface ActionRuntime {
  mode: WriteMode;
  writer: Writer;
  /** null until signed in and the Q&S site id has resolved. */
  io: ActionIO | null;
  /** null until `io` exists. Wired to `writer.outboxBindings(io)` and the mode's storage. */
  outbox: Outbox | null;
  /** Set when the site id could not be resolved (the page should say so; writes cannot run). */
  siteError: string | null;
  /**
   * The eTag the last commit on an item returned, and when (13-09). A verb tapped after an
   * earlier one on the same card has COMMITTED needs it as its If-Match; the outbox chains eTags
   * only between entries still pending (D-13-06-4).
   */
  eTagFor: (itemId: number) => { eTag: string; at: number } | undefined;
}

/**
 * Wraps the writer's outbox bindings so every commit's returned eTag is remembered per item.
 * Shared by the shell's provider and the dev previews.
 */
export function trackCommitETags(
  bindings: OutboxBindings,
  now: () => number = Date.now,
): { bindings: OutboxBindings; eTagFor: ActionRuntime['eTagFor'] } {
  const seen = new Map<number, { eTag: string; at: number }>();
  const commit: OutboxBindings['commit'] = async (entry, progress) => {
    const result = await bindings.commit(entry, progress);
    if (result && typeof result.eTag === 'string') seen.set(entry.itemId, { eTag: result.eTag, at: now() });
    return result;
  };
  return { bindings: { ...bindings, commit }, eTagFor: (itemId) => seen.get(itemId) };
}

export const ActionRuntimeContext = createContext<ActionRuntime | null>(null);

export function useActionRuntime(): ActionRuntime {
  const ctx = useContext(ActionRuntimeContext);
  if (!ctx) throw new Error('useActionRuntime must be used inside <ActionRuntimeProvider>');
  return ctx;
}
